// app/frontend/pages/Demo/ComponentShowcase.tsx
import React, { useState } from "react";
import {
  AppShell,
  Title,
  Grid,
  Stack,
  Paper,
  Burger,
  Group,
  Text,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import VideoCard from "../../components/videos/VideoCard";
import FilterSidebar from "../../components/videos/FilterSidebar";
import type {
  Video,
  UserVideo,
  LearningTrack,
  FilterOptions,
} from "../../types/video";

const ComponentShowcase: React.FC = () => {
  const [opened, { toggle }] = useDisclosure();
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const isTablet = useMediaQuery("(min-width: 600px)");
  const columnSpan = isDesktop ? 3 : isTablet ? 4 : 6;

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<number | null>(null);
  const [selectedSkillLevels, setSelectedSkillLevels] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Demo data (same as before)
  const demoVideos: Video[] = [
    {
      id: 1,
      title: "Basic Step Foundation",
      description:
        "Learn the fundamental basic step that forms the foundation of all other moves",
      thumbnail_url: "https://picsum.photos/640/360?random=1",
      duration: "8:30",
      category: "Technique",
      skill_level: "Beginner - Level 1",
      tags: ["basic", "foundation", "footwork"],
      learning_tracks: ["Beginner Fundamentals"],
      vimeo_id: "123456789",
    },
    {
      id: 2,
      title: "Advanced Spin Combinations",
      description:
        "Complex spinning patterns for experienced dancers with intricate footwork and styling",
      thumbnail_url: "https://picsum.photos/640/360?random=2",
      duration: "20:30",
      category: "Technique",
      skill_level: "Advanced - Level 1",
      tags: ["spins", "advanced", "combinations", "technique", "styling"],
      learning_tracks: ["Advanced Styling"],
      vimeo_id: "123456792",
    },
    {
      id: 3,
      title: "Musicality and Timing",
      description:
        "Develop your sense of rhythm and learn to dance with the music",
      thumbnail_url: "https://picsum.photos/640/360?random=3",
      duration: "15:45",
      category: "Musicality",
      skill_level: "Improver - Level 1",
      tags: ["rhythm", "timing", "musicality", "clave"],
      learning_tracks: ["Intermediate Combinations"],
      vimeo_id: "123456791",
    },
  ];

  const demoLearningTracks: LearningTrack[] = [
    {
      id: 1,
      name: "Beginner Fundamentals",
      description: "Essential moves and concepts for new dancers",
      video_ids: [1],
      estimated_duration: "4 weeks",
      difficulty_range: "Beginner Level 1-2",
    },
    {
      id: 2,
      name: "Advanced Styling",
      description: "Advanced techniques and performance styling",
      video_ids: [2],
      estimated_duration: "8 weeks",
      difficulty_range: "Advanced Level 1-2",
    },
    {
      id: 3,
      name: "Intermediate Combinations",
      description: "Complex patterns and musicality work",
      video_ids: [3],
      estimated_duration: "6 weeks",
      difficulty_range: "Intermediate Level 1-2",
    },
  ];

  const demoFilters: FilterOptions = {
    categories: [
      "Technique",
      "Musicality",
      "Partner Work",
      "Solo Work",
      "Theory",
    ],
    skill_levels: [
      "Beginner - Level 1",
      "Beginner - Level 2",
      "Improver - Level 1",
      "Improver - Level 2",
      "Intermediate - Level 1",
      "Advanced - Level 1",
    ],
    tags: [
      "basic",
      "foundation",
      "footwork",
      "spins",
      "advanced",
      "combinations",
      "technique",
      "styling",
      "rhythm",
      "timing",
      "musicality",
      "clave",
    ],
  };

  const demoUserVideos: Record<number, UserVideo> = {
    1: {
      favorited_at: "2024-01-15T10:30:00Z",
      bookmarked_at: null,
      watched_status: "completed",
    },
    2: {
      favorited_at: null,
      bookmarked_at: "2024-01-16T14:45:00Z",
      watched_status: "in_progress",
    },
  };

  // Filter handlers (same as before)
  const handleCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
    console.log("Category changed:", category);
  };

  const handleTrackChange = (trackId: number | null) => {
    setSelectedTrack(trackId);
    console.log("Track changed:", trackId);
  };

  const handleSkillLevelToggle = (skillLevel: string) => {
    setSelectedSkillLevels((prev) =>
      prev.includes(skillLevel)
        ? prev.filter((level) => level !== skillLevel)
        : [...prev, skillLevel]
    );
    console.log("Skill level toggled:", skillLevel);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    console.log("Tag toggled:", tag);
  };

  const handleClearAll = () => {
    setSelectedCategory(null);
    setSelectedTrack(null);
    setSelectedSkillLevels([]);
    setSelectedTags([]);
    console.log("All filters cleared");
  };

  const handleFavoriteToggle = (videoId: number) => {
    console.log(`Toggle favorite for video ${videoId}`);
  };

  const handleBookmarkToggle = (videoId: number) => {
    console.log(`Toggle bookmark for video ${videoId}`);
  };

  const handleVideoClick = (video: Video) => {
    console.log(`Clicked video: ${video.title}`);
  };

  const closeOnMobile = () => {
    if (window.innerWidth < 768) toggle();
  };

  const appShellStyles = {
    main: {
      background: `
        radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
        linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
      `,
      minHeight: "100vh",
      fontFamily: "'Space Grotesk', sans-serif",
      color: "#F0F0F0",
    },
    header: {
      background: "rgba(0, 0, 0, 0.4)",
      borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
      backdropFilter: "blur(10px)",
    },
    navbar: {
      background: "rgba(0, 0, 0, 0.6)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(15px)",
    },
  };

  return (
    <div style={appShellStyles.main}>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: { base: "85vw", sm: 300 },
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="md"
        styles={{
          main: appShellStyles.main,
          header: appShellStyles.header,
          navbar: appShellStyles.navbar,
        }}
      >
        <AppShell.Header style={appShellStyles.header}>
          <Group h="100%" px="md" justify="space-between">
            <Group>
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
                color="#FF9800"
              />
              <Title
                order={3}
                style={{
                  fontFamily: "'Big Shoulders Display', sans-serif",
                  background: "linear-gradient(45deg, #FFC107, #FF9800)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontWeight: 700,
                }}
              >
                Component Showcase
              </Title>
            </Group>
            <Text
              size="sm"
              c="#B8B8B8"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {demoVideos.length} videos
            </Text>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar>
          <FilterSidebar
            learningTracks={demoLearningTracks}
            filters={demoFilters}
            selectedCategory={selectedCategory}
            selectedTrack={selectedTrack}
            selectedSkillLevels={selectedSkillLevels}
            selectedTags={selectedTags}
            onCategoryChange={(category) => {
              handleCategoryChange(category);
              closeOnMobile();
            }}
            onTrackChange={(trackId) => {
              handleTrackChange(trackId);
              closeOnMobile();
            }}
            onSkillLevelToggle={(skillLevel) => {
              handleSkillLevelToggle(skillLevel);
              closeOnMobile();
            }}
            onTagToggle={(tag) => {
              handleTagToggle(tag);
              closeOnMobile();
            }}
            onClearAll={() => {
              handleClearAll();
              closeOnMobile();
            }}
            videoCounts={{
              Technique: 2,
              Musicality: 1,
              "Partner Work": 0,
              "Solo Work": 0,
              Theory: 0,
            }}
          />
        </AppShell.Navbar>

        <AppShell.Main>
          <Stack gap="xl">
            <Title
              order={1}
              c="#F0F0F0"
              mb="sm"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700 }}
            >
              Video Library
            </Title>
            <Grid>
              {demoVideos.map((video) => (
                <Grid.Col key={video.id} span={columnSpan}>
                  <VideoCard
                    video={video}
                    userVideo={demoUserVideos[video.id]}
                    onFavoriteToggle={handleFavoriteToggle}
                    onBookmarkToggle={handleBookmarkToggle}
                    onVideoClick={handleVideoClick}
                  />
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </AppShell.Main>
      </AppShell>
    </div>
  );
};

export default ComponentShowcase;
